import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import {
  Button,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shared/ui';
import type { Invoice } from '@entities/invoice';
import { formatAmount } from '@shared/lib/format-amount';
import { formatDateTime } from '@shared/lib/format-date';
import { InvoiceStatusBadge } from '@entities/invoice';
import { resolveNetworkIconSrc } from '@shared/lib/network-icons';

interface InvoicesTableProps {
  invoices: Invoice[];
  projectNameById: Record<string, string>;
  networkCodeByCurrencyId: Record<string, string>;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  isLoading?: boolean;
  onRefresh?: () => void;
  onPageChange?: (page: number) => void;
  onRowClick?: (invoice: Invoice) => void;
}

export function InvoicesTable({
  invoices,
  projectNameById,
  networkCodeByCurrencyId,
  pagination,
  isLoading = false,
  onRefresh,
  onPageChange,
  onRowClick,
}: InvoicesTableProps) {
  const { t } = useTranslation('cabinet');

  const renderNetworkBadge = (invoice: Invoice) => {
    const networkCode = networkCodeByCurrencyId[invoice.currency.id ?? ''];
    const iconSrc = resolveNetworkIconSrc(networkCode);
    if (!iconSrc) return null;

    return (
      <img
        src={iconSrc}
        alt=""
        title={networkCode}
        aria-hidden
        className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-background ring-1 ring-background sm:h-3 sm:w-3"
      />
    );
  };

  return (
    <div className="surface-card">
      <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4 sm:gap-3">
        <h2 className="section-title">{t('payments.tableTitle')}</h2>
        <Button
          variant="outline"
          size="icon-sm"
          className="rounded-full sm:size-10"
          onClick={onRefresh}
          loading={isLoading}
          leftIcon={<RefreshCw className="size-4" />}
          aria-label={t('common.retry', { ns: 'common' })}
        />
      </div>

      {isLoading && !invoices.length ? (
        <div className="space-y-2 sm:space-y-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-9 w-full sm:h-10" />
          ))}
        </div>
      ) : !invoices.length ? (
        <div className="py-10 text-center font-sans text-xs text-muted-foreground sm:py-16 sm:text-sm">
          {t('payments.empty')}
        </div>
      ) : (
        <>
          <div className="-mx-1 min-w-0 px-1">
            <Table className="table-compact">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('payments.columns.project')}</TableHead>
                  <TableHead>{t('payments.columns.currency')}</TableHead>
                  <TableHead className="text-right">{t('payments.columns.amount')}</TableHead>
                  <TableHead>{t('payments.columns.status')}</TableHead>
                  <TableHead>{t('payments.columns.date')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow
                    key={invoice.id}
                    className="cursor-pointer"
                    onClick={() => onRowClick?.(invoice)}
                  >
                    <TableCell className="max-w-[7rem] truncate font-medium sm:max-w-none">
                      {projectNameById[invoice.projectId] || invoice.projectId}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 sm:gap-1.5">
                        <span>{invoice.currency.shortCode}</span>
                        {invoice.currency.iconBase && (
                          <span className="relative inline-flex shrink-0">
                            <img
                              src={invoice.currency.iconBase}
                              alt={invoice.currency.shortCode}
                              className="h-4 w-4 sm:h-5 sm:w-5"
                            />
                            {renderNetworkBadge(invoice)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right font-medium">
                      {formatAmount(invoice.amount, 4, true)}
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusBadge
                        status={invoice.status}
                        className="px-2 py-0.5 text-[10px] sm:px-2.5 sm:text-xs"
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatDateTime(invoice.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Pagination
            page={pagination.page}
            limit={pagination.limit}
            total={pagination.total}
            onPageChange={(page) => onPageChange?.(page)}
          />
        </>
      )}
    </div>
  );
}
